// Invites a Paychex Perks user who came back from the rollover tile in the
// Paychex portal to open the IRA their 401(k) funds need. Suppression rules:
// unopenedFirstRolloverTypesSelector. Distinct from PaychexSecondRolloverCard,
// which picks up once a rollover account exists; the two never render together.
import React from "react";
import { get } from "lodash";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { FiPlusCircle } from "react-icons/fi";
import { accountConstants, userConstants } from "actions/types";
import { initializePaychexRollover } from "actions/userActions";
import { setAccountSession } from "actions/accountActions";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import {
  unopenedFirstRolloverTypesSelector,
  type UnopenedRolloverAccountType,
} from "store/selectors/user";
import {
  accountTypesToEnglishMapping,
  rolloverAccountTypesToEnglishMapping,
} from "statics/accountTypes";
import type { RootState } from "types/state";

import Button from "components/Button";
import IconBanner from "components/IconBanner";

const actions = [
  userConstants.INITIALIZE_PAYCHEX_ROLLOVER,
  accountConstants.SET_ACCOUNT_SESSION,
];

const loadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  return {
    unopenedAccountTypes: unopenedFirstRolloverTypesSelector(state),
    isSubmitting: loadingSelector(state),
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  initializePaychexRollover,
  setAccountSession,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

class PaychexFirstRolloverCard extends React.Component<Props> {
  _openAccount = (accountType: UnopenedRolloverAccountType) => {
    window.analytics.track("Paychex First Rollover Started", { accountType });

    void this.props
      .initializePaychexRollover(
        this.props.client as ApolloClient<object>,
        accountType
      )
      .then(async (response) => {
        // dispatchFullLifecycle resolves to the success OR failure action; only
        // success carries `data`, so a missing id means errorSelector has the
        // failure and renders the alert below.
        const accountId = get(response, "data.initializePaychexRollover.id") as
          | string
          | undefined;

        if (!accountId) {
          return;
        }

        await this.props.setAccountSession(
          this.props.client as ApolloClient<object>,
          accountId
        );

        window.analytics.track("Submitted Paychex First Rollover Account Type");
        // PrivateRoute onboards off the active account session, which is only
        // re-read on a fresh boot
        window.location.reload();
      })
      .catch((error: unknown) => {
        console.error("An error occurred:", error);
      });
  };

  render() {
    const { unopenedAccountTypes } = this.props;

    if (unopenedAccountTypes.length === 0) {
      return null;
    }

    const funds = unopenedAccountTypes
      .map((type) => rolloverAccountTypesToEnglishMapping[type].toLowerCase())
      .join(" and ");

    const accounts = unopenedAccountTypes
      .map((type) => accountTypesToEnglishMapping[type])
      .join(" and a ");

    const subheader = `Your ${funds} savings need a ${accounts} to keep the same tax benefits — open one to roll them over.`;

    return (
      <IconBanner
        icon={<FiPlusCircle color="white" stroke="#60A4BF" size={16} />}
        color="primary"
        title="Roll over your Paychex 401(k)."
        subheader={subheader}
        error={!!this.props.error}
        errorMsg={this.props.error}
        actionComponent={unopenedAccountTypes.map((accountType) => (
          <Button
            key={accountType}
            size="sm"
            color="action"
            name="action"
            btnLabel={`Open ${accountTypesToEnglishMapping[accountType]}`}
            loading={this.props.isSubmitting}
            onClick={() => this._openAccount(accountType)}
          />
        ))}
      />
    );
  }
}

export default connector(withApollo<Props>(PaychexFirstRolloverCard));
